import React from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

export interface MobilePeriodOption {
  id: string;
  label: string;
  isCurrent?: boolean;
}

interface MobilePeriodSelectorProps {
  options: MobilePeriodOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  onPrev?: () => void;
  onNext?: () => void;
  iconColor?: string;
  accentColor?: string;
  lightTheme?: boolean;
}

export const MobilePeriodSelector: React.FC<MobilePeriodSelectorProps> = ({
  options,
  selectedId,
  onSelect,
  onPrev,
  onNext,
  iconColor = 'text-cyan-400',
  accentColor = 'cyan',
  lightTheme = false,
}) => {
  const effectiveIconColor = lightTheme
    ? accentColor === 'emerald'
      ? 'text-emerald-600'
      : 'text-slate-600'
    : iconColor;

  return (
    <div className="flex items-center gap-1.5 w-full">
      {onPrev && (
        <button
          type="button"
          onClick={onPrev}
          className={`h-8 w-8 rounded-lg inline-flex items-center justify-center shrink-0 cursor-pointer active:scale-95 transition-all ${
            lightTheme
              ? 'bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200/90 shadow-xs'
              : 'bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700/80'
          }`}
          title="Previous Period"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}

      <div className="relative flex-1 min-w-0">
        <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
          <Calendar className={`w-3.5 h-3.5 ${effectiveIconColor}`} />
        </div>
        <select
          value={selectedId}
          onChange={(e) => onSelect(e.target.value)}
          className={`w-full h-8 pl-8 pr-7 rounded-lg text-xs font-medium focus:outline-none truncate cursor-pointer appearance-none ${
            lightTheme
              ? 'bg-white border border-slate-200/90 text-slate-800 focus:border-slate-400 shadow-xs'
              : 'bg-slate-900/90 border border-slate-700/80 text-slate-200 focus:border-cyan-500/60'
          }`}
        >
          {options.map((opt) => (
            <option
              key={opt.id}
              value={opt.id}
              className={lightTheme ? 'bg-white text-slate-800' : 'bg-slate-900 text-slate-200'}
            >
              {opt.label} {opt.isCurrent ? '(Current)' : ''}
            </option>
          ))}
        </select>
        <div className={`absolute inset-y-0 right-0 pr-2 flex items-center pointer-events-none ${
          lightTheme ? 'text-slate-400' : 'text-slate-400'
        }`}>
          <ChevronRight className="w-3.5 h-3.5 rotate-90" />
        </div>
      </div>

      {onNext && (
        <button
          type="button"
          onClick={onNext}
          className={`h-8 w-8 rounded-lg inline-flex items-center justify-center shrink-0 cursor-pointer active:scale-95 transition-all ${
            lightTheme
              ? 'bg-white hover:bg-slate-100 text-slate-600 hover:text-slate-900 border border-slate-200/90 shadow-xs'
              : 'bg-slate-900/90 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700/80'
          }`}
          title="Next Period"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};
